import {
  Box,
  createTheme,
  Divider,
  Stack,
  Theme,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CompoundHeader } from '@/components/display/CompoundHeader/CompoundHeader';
import { Button } from '@/components/form/baseInputs/Button';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { COMMON_THEME_PROPERTIES } from '@/styles/themes/common';
import { ColorKey, makeThemeFromBrand } from '@/styles/themes/themes.utils';
import { PresentationHeaderFontFace } from '@/types/schema';

import {
  colorInputDefinitionsDataviz,
  colorInputDefinitionsPrimary,
} from './AdminBrandingForm.constants';
import { FirmBrandingFormShape } from './AdminBrandingForm.types';
import { AdminBrandingPreview } from './AdminBrandingPreview';
import { AdminLogosForm } from './AdminLogosForm';
import { BrandingColorInput } from './BrandingColorInput';
import { AdminBrandingPage_BrandingFragment } from './graphql/AdminBrandingPage.generated';
import { SlideConfigForm } from './SlideConfigForm';
import { SlideConfigVariant } from './SlideConfigForm.types';

export interface AdminBrandingFormProps {
  tenantBranding: AdminBrandingPage_BrandingFragment | null;
  onSubmit: () => void;
  /**
   * The currently selected sub-brand ID, or null when editing the
   * tenant-default branding.
   */
  selectedSubBrandId: string | null;
  /**
   * Fires when the OR-aggregate of every form on this page changes.
   * Used by the page to gate the sub-brand switch prompt.
   */
  onAnyDirtyChange?: (dirty: boolean) => void;
  /**
   * Fires when any form on this page is mid-save. Used by the page to
   * hard-block the sub-brand switch while a mutation is in flight.
   */
  onAnySubmittingChange?: (submitting: boolean) => void;
}

enum AdminBrandingTab {
  COLORS = 'colors',
  LOGOS = 'logos',
  COVER_SLIDE = 'cover_slide',
  DIVIDER_SLIDE = 'divider_slide',
}

// The color parsing utilities throw if the color isn't valid, so we need to ensure that the color is valid
function getSafeColor(color: string): string {
  const isValidHex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (!color || !isValidHex.test(color)) {
    return '#FFFFFF';
  }
  return color;
}

function getThemeFromConfiguredColors(values: FirmBrandingFormShape): Theme {
  const brandColors: Record<ColorKey, string> = {
    PRIMARY: getSafeColor(values.primaryColor),
    SECONDARY: getSafeColor(values.secondaryColor),
    BUTTONS_LINKS: getSafeColor(values.buttonsLinkColor),
    DATA_VISUALIZATION_PRIMARY: getSafeColor(
      values.dataVisualizationPrimaryColor
    ),
    DATA_VISUALIZATION_SECONDARY: getSafeColor(
      values.dataVisualizationSecondaryColor
    ),
    DATA_VISUALIZATION_TERTIARY: getSafeColor(
      values.dataVisualizationTertiaryColor
    ),
    DATA_VISUALIZATION_NEGATIVE: getSafeColor(
      values.dataVisualizationNegativeColor
    ),
  };

  const theme = makeThemeFromBrand(
    brandColors,
    {},
    values.presentationHeaderFontFace
  );
  return createTheme(COMMON_THEME_PROPERTIES, theme);
}

export function AdminBrandingForm({
  tenantBranding,
  onSubmit,
  selectedSubBrandId,
  onAnyDirtyChange,
  onAnySubmittingChange,
}: AdminBrandingFormProps) {
  const { activeTab, setActiveTab } = useActiveTab<AdminBrandingTab>({
    defaultTab: AdminBrandingTab.LOGOS,
  });

  // Header font face is a branding form field, saved via the Save button
  // alongside the colors (see the Custom colors tab).
  const fontFaceOptions = useMemo(
    () => [
      { display: 'Romie (default)', value: PresentationHeaderFontFace.Default },
      { display: 'Arial', value: PresentationHeaderFontFace.Arial },
    ],
    []
  );

  const tabOptions = useMemo(
    () => [
      { display: 'Brand logos', value: AdminBrandingTab.LOGOS },
      { display: 'Custom colors', value: AdminBrandingTab.COLORS },
      { display: 'Cover slide', value: AdminBrandingTab.COVER_SLIDE },
      { display: 'Divider slide', value: AdminBrandingTab.DIVIDER_SLIDE },
    ],
    []
  );

  const {
    control,
    watch,
    reset: resetColors,
    formState: { isSubmitting, isDirty: colorsIsDirty },
  } = useFormContext<FirmBrandingFormShape>();

  const values = watch();
  const configuredTheme = getThemeFromConfiguredColors(values);

  // The colors form is reset by the post-save Apollo cache update
  // (see AdminBrandingPage), so `isDirty` flips back to false on
  // success. A failed save doesn't update the cache, so the form
  // stays dirty. `!isSubmitting` just suppresses the prompt during
  // the in-flight window of an active save.
  const colorsDirty = colorsIsDirty && !isSubmitting;

  const [coverDirty, setCoverDirty] = useState(false);
  const [dividerDirty, setDividerDirty] = useState(false);
  const [coverSubmitting, setCoverSubmitting] = useState(false);
  const [dividerSubmitting, setDividerSubmitting] = useState(false);

  const onCoverDirtyChange = useCallback(
    (dirty: boolean) => setCoverDirty(dirty),
    []
  );
  const onDividerDirtyChange = useCallback(
    (dirty: boolean) => setDividerDirty(dirty),
    []
  );
  const onCoverSubmittingChange = useCallback(
    (submitting: boolean) => setCoverSubmitting(submitting),
    []
  );
  const onDividerSubmittingChange = useCallback(
    (submitting: boolean) => setDividerSubmitting(submitting),
    []
  );

  // The form whose dirty state matters when the user *leaves* a given
  // tab. Logos has no form; the other three each own a single form.
  const dirtyByTab: Record<AdminBrandingTab, boolean> = {
    [AdminBrandingTab.LOGOS]: false,
    [AdminBrandingTab.COLORS]: colorsDirty,
    [AdminBrandingTab.COVER_SLIDE]: coverDirty,
    [AdminBrandingTab.DIVIDER_SLIDE]: dividerDirty,
  };

  const isAnyDirty = colorsDirty || coverDirty || dividerDirty;
  useEffect(() => {
    onAnyDirtyChange?.(isAnyDirty);
  }, [isAnyDirty, onAnyDirtyChange]);

  // `isSubmitting` here is the colors form (this component lives inside
  // its FormProvider). Cover/divider report up through their callbacks.
  const isAnySubmitting = isSubmitting || coverSubmitting || dividerSubmitting;
  useEffect(() => {
    onAnySubmittingChange?.(isAnySubmitting);
  }, [isAnySubmitting, onAnySubmittingChange]);

  const [pendingTab, setPendingTab] = useState<AdminBrandingTab | null>(null);

  const handleTabChange = (next: AdminBrandingTab) => {
    if (next === activeTab) return;
    // Hard-block tab switches while any save is in flight — switching
    // would unmount the active form mid-mutation and leave the user
    // unable to react to a failure.
    if (isAnySubmitting) return;
    if (dirtyByTab[activeTab]) {
      setPendingTab(next);
      return;
    }
    setActiveTab(next);
  };

  const handleConfirmationClose = (reason: 'confirm' | 'cancel') => {
    if (reason === 'cancel' && pendingTab !== null) {
      // "Leave" — discard the leaving tab's changes and switch.
      // For cover/divider, the SlideConfigForm unmounts on tab change
      // (its `useForm` lives inside the component), which naturally
      // discards. The colors form is always mounted, so reset it
      // explicitly.
      if (activeTab === AdminBrandingTab.COLORS) {
        resetColors();
      }
      setActiveTab(pendingTab);
    }
    setPendingTab(null);
  };

  return (
    <>
      <Box width="fit-content" mb={4}>
        <ButtonGroup
          label="Branding section"
          hideLabel
          options={tabOptions}
          value={activeTab}
          onChange={(_e, value) => handleTabChange(value)}
        />
      </Box>
      {activeTab === AdminBrandingTab.LOGOS && (
        <Stack spacing={2}>
          <CompoundHeader
            heading="Firm logos"
            subheading="Image files must be in a supported file type (jpg, png, or gif) and cannot be larger than 2MB."
          />
          <Divider />
          <AdminLogosForm tenantBranding={tenantBranding} />
        </Stack>
      )}
      {activeTab === AdminBrandingTab.COLORS && (
        <Stack
          component="form"
          spacing={2}
          maxWidth={1200}
          onSubmit={onSubmit}
          noValidate
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <CompoundHeader
              heading="Custom colors"
              subheading="Customize your presentations by applying your firm's brand colors to the options below."
            />
            <Button
              size="sm"
              variant="primary"
              type="submit"
              loading={isSubmitting}
            >
              Save changes
            </Button>
          </Stack>
          <Divider />
          <Stack spacing={1} maxWidth={320}>
            <Typography variant="title-s">Slide header font</Typography>
            <FormAwareSelectInput<FirmBrandingFormShape>
              fieldName="presentationHeaderFontFace"
              control={control}
              options={fontFaceOptions}
              showEmptyValue={false}
              label="Slide header font"
              hideLabel
              contextualHelp={
                <PopperContent body="Applies to cover slides, divider slides, and all slide headers." />
              }
            />
          </Stack>
          <Divider />
          <Stack spacing={4} mb={2} direction="row" justifyContent="flex-start">
            <Stack spacing={2}>
              <Typography variant="title-s">Brand colors</Typography>
              {colorInputDefinitionsPrimary.map((cid) => (
                <BrandingColorInput
                  {...cid}
                  key={cid.fieldName}
                  control={control}
                  colorValue={values[cid.fieldName] || '#FFFFFF'}
                />
              ))}
            </Stack>

            <Stack spacing={2}>
              <Typography variant="title-s">
                Data visualization colors
              </Typography>
              {colorInputDefinitionsDataviz.map((cid) => (
                <BrandingColorInput
                  {...cid}
                  key={cid.fieldName}
                  control={control}
                  colorValue={values[cid.fieldName] || '#FFFFFF'}
                />
              ))}
            </Stack>
          </Stack>
          <ThemeProvider theme={configuredTheme}>
            <AdminBrandingPreview />
          </ThemeProvider>
        </Stack>
      )}
      {activeTab === AdminBrandingTab.COVER_SLIDE && (
        <SlideConfigForm
          variant={SlideConfigVariant.COVER}
          selectedSubBrandId={selectedSubBrandId}
          onDirtyChange={onCoverDirtyChange}
          onSubmittingChange={onCoverSubmittingChange}
        />
      )}
      {activeTab === AdminBrandingTab.DIVIDER_SLIDE && (
        <SlideConfigForm
          variant={SlideConfigVariant.DIVIDER}
          selectedSubBrandId={selectedSubBrandId}
          onDirtyChange={onDividerDirtyChange}
          onSubmittingChange={onDividerSubmittingChange}
        />
      )}
      {pendingTab !== null && (
        <UnsavedChangesConfirmationModal
          isOpen={true}
          onClose={handleConfirmationClose}
        />
      )}
    </>
  );
}
